export const PRODUCT_ATTRIBUTE_CODES = {
  strength: "strength",
  flavorFamily: "flavor_family",
  pouchSize: "pouch_size",
  experience: "experience",
  formula: "formula",
} as const;

export type ProductAttributeCode = (typeof PRODUCT_ATTRIBUTE_CODES)[keyof typeof PRODUCT_ATTRIBUTE_CODES];

export type StrengthLevel = "BEGINNER" | "FAMILIAR" | "EXPERIENCED";

export type FilterableProduct = {
  productNicotineStrength?: number | null;
  productFlavorFamily?: string | null;
  productPouchSize?: number | null;
  productExperience?: number | null;
  productFormula?: number | null;
  productStrengthDetails?: { rating: number; level: string } | null;
};

const toValues = (value: number | null | undefined): string[] =>
  value === null || value === undefined ? [] : [String(value)];

const splitMultiValue = (value: string | null | undefined): string[] =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

export type AttributeGrouping = "strengthLevel";

export type AttributeConfig = {
  attributeCode: ProductAttributeCode;
  grouping?: AttributeGrouping;
  getValues: (product: FilterableProduct) => string[];
};

export const ATTRIBUTE_CONFIGS = [
  {
    attributeCode: PRODUCT_ATTRIBUTE_CODES.strength,
    grouping: "strengthLevel",
    getValues: (product) => toValues(product.productNicotineStrength),
  },
  {
    attributeCode: PRODUCT_ATTRIBUTE_CODES.flavorFamily,
    getValues: (product) => splitMultiValue(product.productFlavorFamily),
  },
  {
    attributeCode: PRODUCT_ATTRIBUTE_CODES.pouchSize,
    getValues: (product) => toValues(product.productPouchSize),
  },
  {
    attributeCode: PRODUCT_ATTRIBUTE_CODES.experience,
    getValues: (product) => toValues(product.productExperience),
  },
  {
    attributeCode: PRODUCT_ATTRIBUTE_CODES.formula,
    getValues: (product) => toValues(product.productFormula),
  },
] as const satisfies readonly AttributeConfig[];

export const ATTRIBUTE_CONFIG_BY_CODE = ATTRIBUTE_CONFIGS.reduce(
  (acc, config) => {
    acc[config.attributeCode] = config;
    return acc;
  },
  {} as Record<ProductAttributeCode, AttributeConfig>
);

export const isProductAttributeCode = (code: string): code is ProductAttributeCode =>
  Object.hasOwn(ATTRIBUTE_CONFIG_BY_CODE, code);

export const getAttributeConfig = (code: string): AttributeConfig | undefined =>
  isProductAttributeCode(code) ? ATTRIBUTE_CONFIG_BY_CODE[code] : undefined;

export const STRENGTH_LEVEL_ORDER: StrengthLevel[] = ["BEGINNER", "FAMILIAR", "EXPERIENCED"];

export const isStrengthLevel = (value: string): value is StrengthLevel =>
  (STRENGTH_LEVEL_ORDER as string[]).includes(value);

export const STRENGTH_LEVEL_LABEL_KEYS: Record<StrengthLevel, string> = {
  BEGINNER: "plp.filters.strength.level.beginner",
  FAMILIAR: "plp.filters.strength.level.familiar",
  EXPERIENCED: "plp.filters.strength.level.experienced",
};
